import { ChangeEvent, useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import dayjs from 'dayjs';
import { $accountCardListStore } from '@lib/store';
import {
  $transferRequestsUploadFileStore,
  $transferRequestsDeleteFileStore,
  $transferRequestsGetDocumentsStore,
  $transferUpdateStatusStore,
} from '@lib/store/transferRequests';
import { $transferRequestsLimitsStore } from '@lib/store/transferRequests/transferRequestLimitsStore';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import type { RecipientResponse } from '@shared/api/rest/recipients';
import { AmountUtils, DateUtils } from '@shared/utils';
import { Avatar } from './avatar';
import { RecentRecipientIcon } from '../select';
import { SelectAccount } from '@shared/widgets/select';
import { Button, ColorsEnum, FileUpload, Icon, Text, Modal, Spinner } from '@shared/widgets';
import WarningIcon from '@assets/icons/warning.svg';
import { ExchangeRate } from '@shared/widgets/exchangeRate';
import { ApiResponse } from '@shared/api/models';
import { TransferRequestResponse } from '@shared/api/rest/transferRequests';

interface PropsType {
  account: AccountResponse;
  accountTo?: AccountResponse | null;
  recipient?: RecipientResponse | null;
  reasonChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  transactionId?: string;
  amount: number;
  fee?: number;
  reason: string;
  purpose: string;
  exchangeRate?: number;
  isFastPayment: boolean;
  setAccount?: (acc: AccountResponse) => void;
  onConfirm: () => void;
  onClose: (confirm?: boolean) => void;
  isOpen: boolean;
  transferRequest?: ApiResponse<TransferRequestResponse>;
}

export const PaymentModal = (props: PropsType) => {
  const accountStore = useUnit($accountCardListStore.store);
  const uploadedDocs = useUnit($transferRequestsUploadFileStore.store);
  const deleteDocs = useUnit($transferRequestsDeleteFileStore.store);
  const uploadedDocumentsStore = useUnit($transferRequestsGetDocumentsStore.store);
  const updateStatusStore = useUnit($transferUpdateStatusStore.store);
  const transferRequestLimit = useUnit($transferRequestsLimitsStore.store);

  const [selectedItems, setSelectedItems] = useState<File[]>([]);
  const [uploadedIds, setUploadedIds] = useState<string[]>([]);

  useEffect(() => {
    $accountCardListStore.events.update({ AccountType: AccountType.Payment });

    return () => {
      $transferRequestsGetDocumentsStore.events.clear();
    };
  }, []);

  useEffect(() => {
    $transferRequestsLimitsStore.events.update();
  }, [$transferRequestsLimitsStore.store]);

  useEffect(() => {
    setUploadedIds([...uploadedIds, uploadedDocs.data]);
  }, [$transferRequestsUploadFileStore, selectedItems, uploadedDocs.data]);

  useEffect(() => {
    if (props.transactionId) {
      $transferRequestsGetDocumentsStore.events.update({
        TransferRequestId: props.transactionId,
      });
    }
  }, [props.transactionId, $transferRequestsGetDocumentsStore, uploadedDocs.data, $transferRequestsUploadFileStore]);

  const isLimitReached = () => props.amount >= transferRequestLimit.data.outgoingTransferLimit;

  const onFileUpload = async (file: File) => {
    if (props.transactionId) {
      await $transferRequestsUploadFileStore.events.update({
        transferRequestId: props.transactionId,
        file,
      });
    }
  };

  const onFileDelete = async (file: File) => {
    const selectedId = uploadedDocumentsStore?.data?.find((doc) => doc.name === file.name)?.id;

    if (selectedId) {
      $transferRequestsDeleteFileStore.events.update({
        TransferRequestId: props.transactionId!,
        DocumentId: selectedId,
      });
    }
    setUploadedIds(uploadedIds.filter((elem) => elem !== selectedId));
  };

  const onDocDelete = async (id: string) => {
    await $transferRequestsDeleteFileStore.events.update({
      TransferRequestId: props.transactionId!,
      DocumentId: id,
    });
    setUploadedIds(uploadedIds.filter((elem) => elem !== id));
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={
        <>
          <div className='flex justify-between items-center'>
            <Text className='font-[Prata]' size='3xl'>
              {props?.account?.currency && AmountUtils.transformToAmountWithCurrency(props.amount, props.account.currency)}
            </Text>
          </div>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Fee: {AmountUtils.transformToAmountWithCurrency(props.transferRequest?.data?.fee || 0)} {props?.account?.currency}
          </Text>
          <Text className='mb-5' size='sm' color={ColorsEnum.Secondary}>
            {DateUtils.formatDate(dayjs().format(), { withTime: true })}
          </Text>
        </>
      }
      width='500px'
    >
      <hr />
      <div className='flex mt-4 p-3 bg-ice dark:bg-eerie-black mb-3'>
        <Avatar />
        <div className='flex flex-col justify-between ml-3 my-auto'>
          {props.account ? (
            props.account.name ? (
              <>
                <Text weight='semibold'>{props.account.name}</Text>
                {props.account.accountNumber && <Text size='xs'>{props.account.accountNumber}</Text>}
              </>
            ) : (
              <Text weight='semibold'>{props.account.accountNumber || '-'}</Text>
            )
          ) : (
            <SelectAccount
              loading={accountStore.loading}
              accounts={accountStore.data!}
              label='Transfer From'
              currentAccount={props.account}
              type={AccountType.Payment}
              onChange={props.setAccount}
              balanceFieldName='availableBalance'
            />
          )}
        </div>
      </div>
      {props.recipient && (
        <div className='flex p-3 my-auto items-center bg-ice dark:bg-eerie-black'>
          <RecentRecipientIcon
            firstName={props.recipient?.firstName}
            lastName={props.recipient?.lastName}
            companyName={props.recipient?.companyName}
            accountNumber={props.recipient?.accountNumber}
            iban={props.recipient?.iban}
          />
        </div>
      )}
      {props.accountTo && (
        <div className='flex mt-4 p-3  dark:bg-eerie-black bg-ice mb-3'>
          <Avatar />
          <div className='flex flex-col justify-between ml-3 my-auto'>
            {props.accountTo ? (
              props.accountTo.name ? (
                <>
                  <Text weight='semibold'>{props.accountTo.name}</Text>
                  {props.accountTo.accountNumber && <Text size='xs'>{props.accountTo.accountNumber}</Text>}
                </>
              ) : (
                <Text weight='semibold'>{props.accountTo.accountNumber || '-'}</Text>
              )
            ) : (
              <SelectAccount
                loading={accountStore.loading}
                accounts={accountStore.data!}
                label='Transfer From'
                currentAccount={props.account}
                type={AccountType.Payment}
                onChange={props.setAccount}
                balanceFieldName='availableBalance'
              />
            )}
          </div>
        </div>
      )}
      <div className='my-3 flex flex-col gap-1'>
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Sender Currency
          </Text>
          <Text weight='semibold' size='sm'>
            {props.account.currency}
          </Text>
        </div>
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Beneficiary Currency
          </Text>
          <Text weight='semibold' size='sm'>
            {props.recipient ? props.recipient.currency : props.accountTo?.currency}
          </Text>
        </div>
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Sender Amount
          </Text>
          <Text weight='semibold' size='sm'>
            {AmountUtils.transformToAmountWithCurrency(props.amount, props.account.currency)}
          </Text>
        </div>
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Beneficiary Amount
          </Text>
          <Text weight='semibold' size='sm'>
            {AmountUtils.transformToAmountWithCurrency(
              props.amount * props.exchangeRate!,
              props.recipient ? props?.recipient?.currency : props.accountTo?.currency,
            )}
          </Text>
        </div>
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Total Amount
          </Text>
          <Text weight='semibold' size='sm'>
            {AmountUtils.transformToAmountWithCurrency(props.amount + (props.fee || 0), props?.account?.currency)}
          </Text>
        </div>
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Purpose
          </Text>
          <Text weight='semibold' size='sm'>
            {props.purpose}
          </Text>
        </div>
        {(props.recipient ? props.account.currency !== props.recipient.currency : props.account.currency !== props.accountTo?.currency) && (
          <div className='py-1'>
            <Text size='sm' color={ColorsEnum.Secondary}>
              Exchange Rate
            </Text>
            <Text weight='semibold' size='sm'>
              <span className='font-semibold text-sm text-soft-black dark:text-white flex items-center gap-2'>
                {AmountUtils.transformToAmountWithCurrency(1, props.account?.currency)} ={' '}
                {AmountUtils.transformToAmountWithCurrency(
                  props.exchangeRate!,
                  props.recipient ? props.recipient!.currency! : props.accountTo?.currency,
                )}
                <ExchangeRate />
              </span>
            </Text>
          </div>
        )}
        <div className='py-1'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Fast Payment
          </Text>
          <Text weight='semibold' size='sm'>
            {props.isFastPayment ? 'Yes' : 'No'}
          </Text>
        </div>
      </div>
      <div className='relative border-none rounded-sm h-10 pb-0.5 w-full'>
        <Text size='sm' color={ColorsEnum.Secondary}>
          Description
        </Text>
        {props.reasonChange ? (
          <input
            placeholder='Description'
            value={props.reason}
            onChange={props.reasonChange}
            type='text'
            className='appearance-none bg-transparent border-none block w-full h-9 outline-none px-3'
          />
        ) : (
          <Text>{props.reason}</Text>
        )}
      </div>
      {isLimitReached() && (
        <>
          <div className='border-none rounded-sm pb-0.5 w-full bg-ice dark:bg-slate mt-4 p-2'>
            <Text size='sm' color={ColorsEnum.Secondary}>
              Supporting document
            </Text>
            <div className='flex items-start justify-start mt-5'>
              <div className='p-3 bg-light-carmine-pink rounded-sm mr-3'>
                <Icon icon={<WarningIcon />} color='red' />
              </div>
              <div className='flex flex-col items-start justify-start'>
                <Text>
                  To complete the transaction, a supporting documentation is required. Please attach the respective supporting document like but not
                  limited to
                </Text>
                <ul className='mt-2 pl-4'>
                  <li className='list-disc'>Agreements</li>
                  <li className='list-disc'>Invoices</li>
                  <li className='list-disc'>Receipts</li>
                </ul>
              </div>
            </div>
          </div>
          {uploadedDocs.loading || deleteDocs.loading ? (
            <Spinner />
          ) : (
            <FileUpload
              setSelectedItems={setSelectedItems}
              onFileUpload={onFileUpload}
              onFileDelete={onFileDelete}
              uploadedDocs={uploadedDocumentsStore.data}
              onDocDelete={onDocDelete}
            />
          )}
        </>
      )}
      <div className='flex gap-3 mt-6'>
        <Button color='outlined' className='rounded' fullWidth onClick={() => props.onClose()}>
          Return
        </Button>
        <Button
          color='primary'
          className='rounded'
          fullWidth
          disabled={(isLimitReached() && uploadedDocumentsStore.data.length === 0) || updateStatusStore.loading}
          onClick={() => props.onConfirm()}
        >
          Send
        </Button>
      </div>
    </Modal>
  );
};
